/** Single-elimination bracket helpers. */

export type Seed = { id: string; label: string; userId?: string | null };
export type BracketMatch = {
  round: number;
  index: number; // index within the round (0-based)
  player1: Seed | null;
  player2: Seed | null;
};

/** Round seeds to next power of two (with byes). */
export function nextPow2(n: number): number {
  if (n < 2) return 2;
  return 2 ** Math.ceil(Math.log2(n));
}

/** Standard "snake" seeding so 1 plays lowest, 2 plays second lowest, etc. */
export function buildSeeding(seeds: Seed[]): (Seed | null)[] {
  const size = nextPow2(seeds.length);
  const padded: (Seed | null)[] = [...seeds];
  while (padded.length < size) padded.push(null);

  // Build seed order recursively (1, n, n/2+1, n/2, ...)
  const order = (n: number): number[] => {
    if (n === 1) return [0];
    const prev = order(n / 2);
    const out: number[] = [];
    for (const p of prev) {
      out.push(p);
      out.push(n - 1 - p);
    }
    return out;
  };
  return order(size).map(i => padded[i]);
}

/** Given seeded participants in display order, build round 1 matches. */
export function buildRoundOne(orderedSeeds: (Seed | null)[]): BracketMatch[] {
  const matches: BracketMatch[] = [];
  for (let i = 0; i < orderedSeeds.length; i += 2) {
    matches.push({
      round: 1,
      index: i / 2,
      player1: orderedSeeds[i],
      player2: orderedSeeds[i + 1],
    });
  }
  return matches;
}

/** Total number of rounds for a given participant count. */
export function totalRounds(participants: number): number {
  return Math.ceil(Math.log2(nextPow2(participants)));
}

/** Shuffle in-place (Fisher-Yates). */
export function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
